import type { IDiscountActivity } from '@wegic/price-ui';
import type { PromotionDebugMode } from '@/stores/debug';
import { usePromotionStore } from '@wegic/price-ui';
import { logHelper } from '@wegic/utils';
import { getActiveDiscountActivity } from '@/services/pay';
import { requestActivePromotionWidgets } from '@/services/promotion';
import { useDebugStore } from '@/stores/debug';
import { useI18nStore } from '@/stores/i18n';
import { getPromotionDebugRequestConfig } from './initDiscountActivityHelper';

let latestInitRequestId = 0;

/**
 * 初始化促销活动数据
 */
interface InitDiscountActivityOptions {
  debugMode?: PromotionDebugMode;
}

function getCurrentPagePath(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.location.pathname;
}

function applyDiscountActivity(requestId: number, activity: IDiscountActivity | null = null) {
  // 调试面板可能快速切换模式，忽略旧请求的异步结果。
  if (requestId !== latestInitRequestId) {
    return;
  }

  usePromotionStore.getState().setDiscountActivity(activity);
}

export async function initDiscountActivity(options: InitDiscountActivityOptions = {}): Promise<void> {
  const requestId = latestInitRequestId + 1;
  latestInitRequestId = requestId;

  const { promotionDebugMode } = useDebugStore.getState();
  const debugMode = options.debugMode ?? promotionDebugMode;
  const requestConfig = getPromotionDebugRequestConfig(debugMode);

  if (!requestConfig.shouldFetchActiveActivity && !requestConfig.shouldFetchAtmosphereWidgets) {
    applyDiscountActivity(requestId);
    return;
  }

  if (requestConfig.shouldFetchAtmosphereWidgets) {
    // Builder 不渲染 CMS 氛围组件，这里只在调试模式下验证氛围接口链路。
    try {
      await requestActivePromotionWidgets({
        language: useI18nStore.getState().currentLanguage,
        page: getCurrentPagePath(),
      });
    } catch (error) {
      logHelper.error('[initDiscountActivity] load promotion atmosphere failed:', error);
    }
    applyDiscountActivity(requestId);
    return;
  }

  try {
    const res = await getActiveDiscountActivity();
    const backendActivity = res.code === 0 ? res.data ?? null : null;
    applyDiscountActivity(requestId, backendActivity?.code ? backendActivity : null);
  } catch (error) {
    logHelper.error('[initDiscountActivity] load discount activity failed:', error);
    applyDiscountActivity(requestId);
  }
}
